import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import PublicAGoGoBoard from "@/pages/PublicAGoGoBoard";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft, Medal, Trophy, Clock, Users, Calendar, BarChart3,
  Image as ImageIcon, MapPin, FileText, Utensils, PartyPopper,
  Award, Car, ClipboardList, Info, X, Phone, Mail,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";
import ScorecardModal from "@/components/ScorecardModal";

const LOGO_GREEN = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";

const STATUS_LABEL = {
  draft: { txt: "Borrador", cls: "bg-gray-100 text-gray-700" },
  open: { txt: "Abierto", cls: "bg-blue-100 text-blue-700" },
  in_progress: { txt: "En Juego", cls: "bg-green-100 text-green-700" },
  completed: { txt: "Finalizado", cls: "bg-purple-100 text-purple-700" },
};

const EVENT_ICONS = {
  golf: { icon: Award, color: "text-green-600 bg-green-50" },
  food: { icon: Utensils, color: "text-orange-600 bg-orange-50" },
  ceremony: { icon: Trophy, color: "text-amber-600 bg-amber-50" },
  transport: { icon: Car, color: "text-blue-600 bg-blue-50" },
  registration: { icon: ClipboardList, color: "text-purple-600 bg-purple-50" },
  party: { icon: PartyPopper, color: "text-pink-600 bg-pink-50" },
  info: { icon: Info, color: "text-gray-600 bg-gray-50" },
};

export default function PublicTournamentPage() {
  const { slug, tournamentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("convocatoria");

  useEffect(() => {
    fetch(`${API}/public/clubs/${slug}/tournaments/${tournamentId}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || "Error");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, tournamentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center p-6">
        <div className="text-center">
          <Medal className="w-16 h-16 mx-auto text-[#94A3B8] mb-3 opacity-50" />
          <h2 className="font-bold text-[#0F172A] mb-1">{error || "Torneo no encontrado"}</h2>
          <Link to={`/${slug}`} className="text-sm text-[#1B4D3E] hover:underline mt-3 inline-block">Ver club</Link>
        </div>
      </div>
    );
  }

  const { club, tournament, overall, by_category, teetimes, events, photos, field } = data;

  if (tournament.scoring_format === "a_go_go") {
    return <PublicAGoGoBoard slug={slug} tournamentId={tournamentId} club={club} tournament={tournament} />;
  }

  const isLive = ["open", "in_progress"].includes(tournament.status);
  const isNet = tournament.mode === "net";
  const st = STATUS_LABEL[tournament.status] || STATUS_LABEL.draft;

  const ogImage = club.logo_url || LOGO_GREEN;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = `${origin}/api/public/share/${slug}/tournaments/${tournamentId}`;
  const ogTitle = `${tournament.name} — ${club.name}`;
  const ogDesc = (isLive ? "EN VIVO · " : "") + `${st.txt} · ${tournament.rounds} ronda${tournament.rounds > 1 ? "s" : ""} · ${isNet ? `NET ${tournament.hcp_percent}%` : "GROSS"}`;

  const tabs = [
    { k: "convocatoria", l: "Convocatoria", icon: FileText },
    { k: "field", l: "Field", icon: Users, count: (field || []).length },
    { k: "leaderboard", l: "Leaderboard", icon: BarChart3 },
    { k: "teetimes", l: "Tee Times", icon: Clock, count: teetimes.length },
    { k: "events", l: "Eventos", icon: Calendar, count: events.length },
    { k: "gallery", l: "Galeria", icon: ImageIcon, count: photos.length },
  ];

  return (
    <div className="min-h-screen bg-[#F5F4F0]" style={{ fontFamily: "Outfit, sans-serif" }}>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDesc} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}`} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A]">
            <ArrowLeft className="w-4 h-4" /> {club.name}
          </Link>
          <img src={LOGO_GREEN} alt="cadii" className="h-7 rounded" />
        </div>
      </header>

      {/* Tournament Hero */}
      <section className="bg-[#1B4D3E] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Medal className="w-5 h-5 text-white/70" strokeWidth={1.5} />
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.txt}</span>
                <span className="text-xs bg-white/15 text-white px-2 py-0.5 rounded font-medium">{isNet ? `NET ${tournament.hcp_percent}%` : "GROSS"}</span>
                {isLive && (
                  <span className="flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> EN VIVO
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="public-tournament-title">{tournament.name}</h1>
              <div className="flex flex-wrap gap-4 text-white/70 text-sm mt-2">
                {tournament.course_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {tournament.course_name}</span>}
                {tournament.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {tournament.start_date}{tournament.end_date && tournament.end_date !== tournament.start_date && ` → ${tournament.end_date}`}</span>}
                <span>{tournament.rounds} ronda{tournament.rounds > 1 ? "s" : ""}</span>
                <span>Par {tournament.par_total}</span>
              </div>
            </div>
            <ShareButton title={ogTitle} text={ogDesc} url={shareUrl} variant="pill" label="Compartir torneo" testId="share-tournament-btn" />
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-2 sm:px-6 overflow-x-auto">
          <div className="flex gap-1" data-testid="public-tabs">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 ${tab === t.k ? "border-[#1B4D3E] text-[#1B4D3E]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}
                  data-testid={`public-tab-${t.k}`}>
                  <Icon className="w-4 h-4" /> {t.l}
                  {typeof t.count === "number" && t.count > 0 && (
                    <span className="text-[10px] bg-gray-100 text-[#64748B] px-1.5 rounded-full">{t.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {tab === "convocatoria" && <Convocatoria tournament={tournament} />}
        {tab === "field" && <Field tournament={tournament} field={field || []} />}
        {tab === "leaderboard" && (
          <Leaderboard tournament={tournament} overall={overall} byCategory={by_category} isNet={isNet} slug={slug} tournamentId={tournamentId} />
        )}
        {tab === "teetimes" && <TeeTimes teetimes={teetimes} />}
        {tab === "events" && <Events events={events} />}
        {tab === "gallery" && <Gallery photos={photos} />}
      </main>

      <footer className="bg-white border-t border-gray-100 mt-12 py-6">
        <div className="max-w-5xl mx-auto px-6 text-center space-y-1">
          <p className="text-xs text-[#94A3B8]">Powered by <span className="font-semibold text-[#1B4D3E]">cadii</span></p>
          <Link
            to="/club/login"
            className="inline-block text-[10px] tracking-wide text-[#CBD5E1] hover:text-[#1B4D3E] transition-colors"
            data-testid="master-login-link"
          >
            mastercadii
          </Link>
        </div>
      </footer>
    </div>
  );
}

// "2026-06-17" → "Vie 17 Jun" (timezone-safe).
function fmtDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

function fmtTimeRange(start, end) {
  if (start && end) return `${start} – ${end}`;
  if (start) return `desde ${start}`;
  if (end) return `hasta ${end}`;
  return "Hora por confirmar";
}

function Convocatoria({ tournament }) {
  const cats = tournament.categories || [];
  // Prefer new image field; fall back to legacy pdf field for older tournaments.
  const imageUrl = tournament.convocatoria_image_url || null;
  const imageName = tournament.convocatoria_image_name || "";
  const legacyPdfUrl = !imageUrl ? tournament.convocatoria_pdf_url : null;
  const legacyPdfName = tournament.convocatoria_pdf_name || "";
  const rulesText = tournament.local_rules_text;
  const rulesPdfUrl = tournament.local_rules_pdf_url;
  const contacts = tournament.contacts || [];

  // Group calendar by date for a chronological view.
  const byDate = {};
  for (const c of cats) {
    for (const d of (c.playing_days || [])) {
      const times = (c.playing_times || {})[d] || {};
      byDate[d] = byDate[d] || [];
      byDate[d].push({ category: c.name, start: times.start_time, end: times.end_time });
    }
  }
  const dates = Object.keys(byDate).sort();
  const hasSchedule = dates.length > 0;
  const hasAnything = imageUrl || legacyPdfUrl || hasSchedule || rulesText || rulesPdfUrl || contacts.length > 0;

  if (!hasAnything) {
    return (
      <Card className="card-default">
        <CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-3 opacity-40" strokeWidth={1.5} />
          <p className="text-[#94A3B8]">La convocatoria de este torneo aún no está publicada</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="public-convocatoria">
      {imageUrl && (
        <Card className="card-default overflow-hidden">
          <img
            src={imageUrl}
            alt={imageName || "Convocatoria"}
            className="w-full h-auto max-h-[80vh] object-contain bg-gray-50 cursor-zoom-in"
            data-testid="public-convocatoria-image"
            onClick={() => window.open(imageUrl, "_blank")}
          />
        </Card>
      )}
      {legacyPdfUrl && (
        <Card className="card-default">
          <CardContent className="p-5">
            <a href={legacyPdfUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg bg-[#1B4D3E]/5 border border-[#1B4D3E]/15 hover:bg-[#1B4D3E]/10 transition-colors"
              data-testid="public-convocatoria-pdf">
              <div className="w-10 h-10 rounded-full bg-[#1B4D3E] text-white flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#0F172A]">Convocatoria del torneo</div>
                <div className="text-xs text-[#64748B] truncate">{legacyPdfName || "Ver / Descargar PDF"}</div>
              </div>
              <span className="text-xs font-semibold text-[#1B4D3E] whitespace-nowrap">Abrir PDF →</span>
            </a>
          </CardContent>
        </Card>
      )}

      {hasSchedule && (
        <Card className="card-default">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-[#1B4D3E]" /> Calendario de juego
            </h3>
            <div className="space-y-3">
              {dates.map((d) => (
                <div key={d} className="border border-gray-100 rounded-lg p-3" data-testid={`public-day-${d}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#1B4D3E] capitalize mb-2">{fmtDay(d)}</div>
                  <div className="space-y-1.5">
                    {byDate[d].map((row, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-medium text-[#0F172A]">{row.category}</span>
                        <span className="text-[#64748B] text-xs whitespace-nowrap">{fmtTimeRange(row.start, row.end)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(rulesText || rulesPdfUrl) && (
        <Card className="card-default">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-[#1B4D3E]" /> Reglas locales
            </h3>
            {rulesText && (
              <p className="text-sm text-[#334155] whitespace-pre-line leading-relaxed mb-3" data-testid="public-rules-text">
                {rulesText}
              </p>
            )}
            {rulesPdfUrl && (
              <a href={rulesPdfUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#1B4D3E] hover:underline"
                data-testid="public-rules-pdf">
                <FileText className="w-4 h-4" /> {tournament.local_rules_pdf_name || "Ver reglas (PDF)"} →
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {contacts.length > 0 && (
        <Card className="card-default">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#1B4D3E]" /> Contactos del torneo
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contacts.map((c, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3" data-testid={`public-contact-${i}`}>
                  {c.name && <div className="text-sm font-bold text-[#0F172A]">{c.name}</div>}
                  {c.role && <div className="text-xs text-[#64748B] mb-1.5">{c.role}</div>}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-xs text-[#334155] hover:text-[#1B4D3E]">
                      <Phone className="w-3 h-3" /> {c.phone}
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-[#334155] hover:text-[#1B4D3E]">
                      <Mail className="w-3 h-3" /> {c.email}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({ tournament, field }) {
  const cats = tournament.categories || [];
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const counts = useMemo(() => {
    const c = { all: field.length, none: 0 };
    for (const p of field) {
      if (!p.category_id) c.none += 1;
      else c[p.category_id] = (c[p.category_id] || 0) + 1;
    }
    return c;
  }, [field]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return field.filter((p) => {
      if (catFilter === "none" && p.category_id) return false;
      if (catFilter !== "all" && catFilter !== "none" && p.category_id !== catFilter) return false;
      if (term && !(p.name || "").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [field, q, catFilter]);

  if (field.length === 0) {
    return (
      <Card className="card-default">
        <CardContent className="py-12 text-center text-[#94A3B8]">Sin competidores inscritos aún</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="public-field">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="public-field-count">
          {filtered.length} de {field.length} competidor{field.length === 1 ? "" : "es"}
        </span>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre…"
          className="w-full sm:w-64 h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
          data-testid="public-field-search"
        />
      </div>

      {(cats.length > 0 || counts.none > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="public-field-filter">
          <button
            onClick={() => setCatFilter("all")}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
              catFilter === "all" ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
            }`}
            data-testid="public-field-cat-all"
          >
            Todos <span className="opacity-70 ml-1">{counts.all}</span>
          </button>
          {cats.map((c) => (
            <button
              key={c.category_id}
              onClick={() => setCatFilter(c.category_id)}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === c.category_id ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
              }`}
              data-testid={`public-field-cat-${c.category_id}`}
            >
              {c.name} <span className="opacity-70 ml-1">{counts[c.category_id] || 0}</span>
            </button>
          ))}
          {counts.none > 0 && (
            <button
              onClick={() => setCatFilter("none")}
              className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === "none" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200"
              }`}
              data-testid="public-field-cat-none"
            >
              Sin categoría <span className="opacity-70 ml-1">{counts.none}</span>
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-12 text-center text-[#94A3B8]">Ningún competidor coincide con los filtros</CardContent>
        </Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="public-field-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">COMPETIDOR</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-[#64748B]">INDEX</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-[#64748B]">CATEGORIA</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.player_id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`public-field-row-${p.player_id}`}>
                    <td className="px-4 py-2 font-medium text-[#0F172A]">{p.name}</td>
                    <td className="text-center px-4 py-2 text-[#64748B]">{p.handicap_index ?? "—"}</td>
                    <td className="px-4 py-2 text-[#64748B]">{p.category_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function Leaderboard({ tournament, overall, byCategory, isNet, slug, tournamentId }) {
  const catNames = useMemo(() => Object.keys(byCategory || {}), [byCategory]);
  const [activeCat, setActiveCat] = useState(catNames.length > 0 ? catNames[0] : "__overall__");
  const [nameQuery, setNameQuery] = useState("");
  const [scorecardPid, setScorecardPid] = useState(null);

  const activeEntries = activeCat === "__overall__"
    ? (overall || [])
    : (byCategory[activeCat] || []);
  const activeTitle = activeCat === "__overall__" ? "Clasificación General" : activeCat;
  const globalFormat = tournament.scoring_format || "medal_play";
  const categoryFormat = (name) => {
    const c = (tournament.categories || []).find((x) => x.name === name);
    return c?.scoring_format || globalFormat;
  };
  const activeFormat = activeCat === "__overall__" ? globalFormat : categoryFormat(activeCat);
  const globalMode = tournament.mode || "gross";
  const categoryMode = (name) => {
    const c = (tournament.categories || []).find((x) => x.name === name);
    return c?.mode || globalMode;
  };
  const activeMode = activeCat === "__overall__" ? globalMode : categoryMode(activeCat);
  const term = nameQuery.trim().toLowerCase();
  const filtered = term
    ? activeEntries.filter((e) => (e.name || "").toLowerCase().includes(term))
    : activeEntries;

  const activeCut = activeCat === "__overall__"
    ? null
    : (() => {
        const c = (tournament.categories || []).find((x) => x.name === activeCat);
        return c?.cut && c.cut.enabled ? c.cut : null;
      })();
  const roundsPlayedMax = activeEntries.reduce((m, e) => Math.max(m, e.rounds_played || 0), 0);
  const cutPlayers = activeCut && activeCut.players > 0 && roundsPlayedMax >= activeCut.after_round
    ? activeCut.players
    : null;

  const renderTable = (entries, title, format, mode, cutPlayers) => {
    const isStable = format === "stableford";
    const isNet = mode === "net";
    const colCount = 6 + tournament.rounds + (isNet ? 1 : 0) + (isStable ? 1 : 0);
    const showCut = cutPlayers != null && !term && entries.some((e) => e.made_cut === true) && entries.some((e) => e.made_cut === false);
    const cutAt = showCut ? entries.findIndex((e) => e.made_cut === false) : -1;
    return (
    <Card className="card-default">
      <CardContent className="p-0">
        <div className="bg-[#1B4D3E]/5 px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-sm text-[#1B4D3E]">{title}</h3>
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#1B4D3E]/70">
            {isStable ? "Stableford" : "Medal Play"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="public-lb-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B] w-10">#</th>
                <th className="text-left px-2 py-2 text-xs font-semibold text-[#64748B]">JUGADOR</th>
                <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B] hidden sm:table-cell">IDX</th>
                {Array.from({ length: tournament.rounds }).map((_, i) => (
                  <th key={i} className="text-center px-2 py-2 text-xs font-semibold text-[#64748B] hidden md:table-cell">R{i + 1}</th>
                ))}
                <th className="text-center px-2 py-2 text-xs font-semibold text-[#0F172A]">GROSS</th>
                {isNet && <th className="text-center px-2 py-2 text-xs font-semibold text-[#1B4D3E]">NET</th>}
                {isStable && <th className="text-center px-2 py-2 text-xs font-semibold text-[#1B4D3E]">PTS</th>}
                <th className="text-center px-2 py-2 text-xs font-semibold text-[#64748B]">vs PAR</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan={6 + tournament.rounds + (isNet ? 1 : 0) + (isStable ? 1 : 0)} className="text-center py-6 text-[#94A3B8]">
                  {term ? "Ningún jugador coincide con la búsqueda" : "Sin resultados aun"}
                </td></tr>
              ) : entries.flatMap((e, idx) => {
                const missed = cutAt !== -1 && idx >= cutAt;
                const rowCls = e.result_status
                  ? "bg-gray-50/60"
                  : e.rounds_played === 0
                  ? "opacity-40"
                  : missed
                  ? "opacity-50 bg-gray-50/40 text-[#94A3B8]"
                  : (idx < 3 && e.position && !term ? "bg-amber-50/30" : "");
                const rows = [];
                if (showCut && cutAt > 0 && idx === cutAt) {
                  rows.push(
                    <tr key="cut-line" className="bg-red-50/80 border-t-2 border-red-400" data-testid="public-lb-cut-line">
                      <td colSpan={colCount} className="px-2 py-1.5 text-center">
                        <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-red-600">
                          <span className="h-px w-8 bg-red-400" />
                          Línea de Corte · Pasan {cutPlayers}
                          <span className="h-px w-8 bg-red-400" />
                        </span>
                      </td>
                    </tr>
                  );
                }
                rows.push(
                <tr key={e.player_id} className={`border-t border-gray-50 ${rowCls}`} data-testid={`lb-${e.player_id}`}>
                  <td className="text-center px-2 py-2 font-bold">
                    {e.result_status ? "—" : (e.position ?? "—")}
                    {!e.result_status && e.tiebreak && <span className="block text-[9px] font-semibold text-amber-600 leading-none" title="Resuelto por desempate (countback)">c/b</span>}
                  </td>
                  <td className="px-2 py-2 font-medium text-[#0F172A]">
                    <button type="button" onClick={() => setScorecardPid(e.player_id)} className="text-left hover:text-[#1B4D3E] hover:underline" data-testid={`public-lb-name-${e.player_id}`}>{e.name}</button>
                    {e.result_status && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold ${e.result_status === "DQ" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{e.result_status}</span>}
                    {missed && !e.result_status && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold bg-gray-300 text-gray-700">CUT</span>}
                  </td>
                  <td className="text-center px-2 py-2 text-[#64748B] hidden sm:table-cell">{e.handicap_index ?? "—"}</td>
                  {Array.from({ length: tournament.rounds }).map((_, i) => (
                    <td key={i} className="text-center px-2 py-2 text-[#64748B] hidden md:table-cell">{isStable ? ((e.stableford_rounds || [])[i] ?? "—") : (isNet ? (e.net_rounds[i] ?? "—") : (e.gross_rounds[i] ?? "—"))}</td>
                  ))}
                  <td className="text-center px-2 py-2 font-bold text-[#0F172A]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.gross_total : "—")}</td>
                  {isNet && <td className="text-center px-2 py-2 font-bold text-[#1B4D3E]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.net_total : "—")}</td>}
                  {isStable && <td className="text-center px-2 py-2 font-bold text-[#1B4D3E]">{e.result_status ? "—" : (e.rounds_played > 0 ? e.stableford_total : "—")}</td>}
                  <td className="text-center px-2 py-2 text-[#64748B]">{e.result_status ? "—" : (e.rounds_played > 0 ? fmtPar(isNet ? e.score_to_par_net : e.score_to_par_gross) : "—")}</td>
                </tr>
                );
                return rows;
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
    );
  };

  const chipCls = (active) =>
    `px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
      active ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#64748B] border-gray-200"
    }`;

  return (
    <div className="space-y-3" data-testid="public-lb">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="public-lb-count">
          {filtered.length} de {activeEntries.length} jugador{activeEntries.length === 1 ? "" : "es"}
        </span>
        <input
          type="search"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder="Buscar jugador por nombre…"
          className="w-full sm:w-64 h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
          data-testid="public-lb-search"
        />
      </div>
      {(catNames.length > 0 || (overall || []).length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="public-lb-cat-filter">
          {(catNames.length === 0 || (overall || []).length > 0) && (
            <button onClick={() => setActiveCat("__overall__")} className={chipCls(activeCat === "__overall__")} data-testid="public-lb-cat-overall">
              General <span className="opacity-70 ml-1">{(overall || []).length}</span>
            </button>
          )}
          {catNames.map((cn) => (
            <button key={cn} onClick={() => setActiveCat(cn)} className={chipCls(activeCat === cn)} data-testid={`public-lb-cat-${cn}`}>
              {cn} <span className="opacity-70 ml-1">{byCategory[cn]?.length || 0}</span>
            </button>
          ))}
        </div>
      )}
      {renderTable(filtered, activeTitle, activeFormat, activeMode, cutPlayers)}
      <ScorecardModal
        open={!!scorecardPid}
        onClose={() => setScorecardPid(null)}
        url={scorecardPid ? `${API}/public/clubs/${slug}/tournaments/${tournamentId}/players/${scorecardPid}/scorecard` : null}
      />
    </div>
  );
}

function fmtPar(v) {
  if (v === 0) return "E";
  return v > 0 ? `+${v}` : `${v}`;
}

function TeeTimes({ teetimes }) {
  const [catFilter, setCatFilter] = useState("all");
  const [roundFilter, setRoundFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [nameQuery, setNameQuery] = useState("");

  const { filtered, catOpts, roundOpts, dayOpts, hasUnknownDay } = useMemo(() => {
    const catOpts = new Map();
    const roundOpts = new Map();
    const dayOpts = new Map();
    for (const tt of teetimes) {
      const cn = tt.category_name || "__none__";
      catOpts.set(cn, (catOpts.get(cn) || 0) + 1);
      roundOpts.set(tt.round_num, (roundOpts.get(tt.round_num) || 0) + 1);
      const d = tt.playing_date || "__none__";
      dayOpts.set(d, (dayOpts.get(d) || 0) + 1);
    }
    const term = nameQuery.trim().toLowerCase();
    const filtered = [];
    for (const tt of teetimes) {
      if (catFilter !== "all") {
        const cn = tt.category_name || "__none__";
        if (cn !== catFilter) continue;
      }
      if (roundFilter !== "all" && String(tt.round_num) !== String(roundFilter)) continue;
      if (dayFilter !== "all") {
        const d = tt.playing_date || "__none__";
        if (d !== dayFilter) continue;
      }
      if (!term) { filtered.push(tt); continue; }
      const groups = (tt.groups || []).filter((g) =>
        (g.players || []).some((p) => (p.name || "").toLowerCase().includes(term))
      );
      if (groups.length > 0) filtered.push({ ...tt, groups });
    }
    return {
      filtered,
      catOpts: Array.from(catOpts.entries()).map(([name, count]) => ({ name, count })),
      roundOpts: Array.from(roundOpts.keys()).sort((a, b) => a - b),
      dayOpts: Array.from(dayOpts.keys()).filter((d) => d !== "__none__").sort(),
      hasUnknownDay: dayOpts.has("__none__"),
    };
  }, [teetimes, catFilter, roundFilter, dayFilter, nameQuery]);

  if (teetimes.length === 0) {
    return <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Los tee times se publican cuando el comite los autoriza</CardContent></Card>;
  }

  const chipCls = (active, tone = "green") =>
    `px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-colors ${
      active
        ? tone === "amber"
          ? "bg-amber-500 text-white border-amber-500"
          : "bg-[#1B4D3E] text-white border-[#1B4D3E]"
        : "bg-white text-[#64748B] border-gray-200"
    }`;
  const term = nameQuery.trim().toLowerCase();
  const totalGroups = filtered.reduce((n, tt) => n + (tt.groups?.length || 0), 0);
  const hasCats = catOpts.some((c) => c.name !== "__none__");

  return (
    <div className="space-y-3" data-testid="public-tt">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-[#64748B]" data-testid="public-tt-count">
          {filtered.length} de {teetimes.length} tee time{teetimes.length === 1 ? "" : "s"}
          {term && ` · ${totalGroups} grupo${totalGroups === 1 ? "" : "s"} con coincidencias`}
        </span>
        <input
          type="search"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder="Buscar jugador por nombre…"
          className="w-full sm:w-64 h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#1B4D3E]"
          data-testid="public-tt-search"
        />
      </div>
      <div className="space-y-1.5">
        {hasCats && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="public-tt-filter-cats">
            <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Categoría</span>
            <button onClick={() => setCatFilter("all")} className={chipCls(catFilter === "all")} data-testid="public-tt-cat-all">Todas</button>
            {catOpts.map((c) => c.name === "__none__" ? (
              <button key="none" onClick={() => setCatFilter("__none__")} className={chipCls(catFilter === "__none__", "amber")} data-testid="public-tt-cat-none">
                General <span className="opacity-70 ml-1">{c.count}</span>
              </button>
            ) : (
              <button key={c.name} onClick={() => setCatFilter(c.name)} className={chipCls(catFilter === c.name)} data-testid={`public-tt-cat-${c.name}`}>
                {c.name} <span className="opacity-70 ml-1">{c.count}</span>
              </button>
            ))}
          </div>
        )}
        {roundOpts.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="public-tt-filter-rounds">
            <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Ronda</span>
            <button onClick={() => setRoundFilter("all")} className={chipCls(roundFilter === "all")} data-testid="public-tt-round-all">Todas</button>
            {roundOpts.map((r) => (
              <button key={r} onClick={() => setRoundFilter(String(r))} className={chipCls(String(roundFilter) === String(r))} data-testid={`public-tt-round-${r}`}>
                R{r}
              </button>
            ))}
          </div>
        )}
        {(dayOpts.length > 1 || (dayOpts.length === 1 && hasUnknownDay)) && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="public-tt-filter-days">
            <span className="text-[11px] font-semibold text-[#94A3B8] mr-1 uppercase tracking-wide">Día</span>
            <button onClick={() => setDayFilter("all")} className={chipCls(dayFilter === "all")} data-testid="public-tt-day-all">Todos</button>
            {dayOpts.map((d) => (
              <button key={d} onClick={() => setDayFilter(d)} className={chipCls(dayFilter === d)} data-testid={`public-tt-day-${d}`}>
                <span className="capitalize">{fmtDay(d)}</span>
              </button>
            ))}
            {hasUnknownDay && (
              <button onClick={() => setDayFilter("__none__")} className={chipCls(dayFilter === "__none__", "amber")} data-testid="public-tt-day-none">
                Sin fecha
              </button>
            )}
          </div>
        )}
      </div>
      {filtered.length === 0 ? (
        <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">
          Ningún tee time coincide con los filtros
        </CardContent></Card>
      ) : filtered.map((tt) => (
        <Card key={tt.teetime_id} className="card-default" data-testid={`pub-tt-${tt.round_num}`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <h3 className="font-bold text-[#0F172A]">
                Ronda {tt.round_num}
                {tt.category_name && <span className="text-[#64748B] font-normal"> · {tt.category_name}</span>}
              </h3>
              {tt.playing_date && (
                <span className="text-xs text-[#64748B] capitalize">{fmtDay(tt.playing_date)}</span>
              )}
            </div>
            <div className="space-y-2">
              {tt.groups.map((g) => (
                <div key={g.group_number} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="min-w-[58px] text-center">
                    <p className="text-[10px] text-[#94A3B8]">G{g.group_number}</p>
                    <p className="font-bold text-[#0F172A]">{g.time}</p>
                    <p className="text-[10px] text-[#94A3B8]">H{g.hole}</p>
                  </div>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
                    {g.players.map((p) => {
                      const match = term && (p.name || "").toLowerCase().includes(term);
                      return (
                        <div key={p.player_id} className={`flex items-center gap-2 rounded px-1 ${match ? "bg-amber-100" : ""}`}>
                          <span className={`text-[#0F172A] ${match ? "font-semibold" : ""}`}>{p.name}</span>
                          {p.handicap_index != null && <span className="text-xs text-[#94A3B8]">({p.handicap_index})</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Events({ events }) {
  if (events.length === 0) {
    return <Card className="card-default"><CardContent className="py-12 text-center text-[#94A3B8]">Sin eventos en el calendario</CardContent></Card>;
  }
  return (
    <div className="space-y-3" data-testid="pub-events">
      {events.map((e) => {
        const cfg = EVENT_ICONS[e.icon] || EVENT_ICONS.info;
        const Icon = cfg.icon;
        const dt = e.datetime ? new Date(e.datetime) : null;
        return (
          <Card key={e.event_id} className="card-default">
            <CardContent className="p-4 flex items-start gap-3">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-[#0F172A]">{e.title}</h4>
                {dt && (
                  <p className="text-xs text-[#1B4D3E] font-medium mt-0.5">
                    {dt.toLocaleDateString("es", { weekday: "short", day: "numeric", month: "short" })} · {dt.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                )}
                {e.location && <p className="text-xs text-[#64748B] mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</p>}
                {e.description && <p className="text-sm text-[#64748B] mt-1">{e.description}</p>}
                {e.attachment_url && (
                  <a href={e.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#1B4D3E] hover:underline">
                    <FileText className="w-3 h-3" /> {e.attachment_name || "Ver archivo"}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function Gallery({ photos }) {
  const [lightbox, setLightbox] = useState(null);
  if (photos.length === 0) {
    return <Card className="card-default"><CardContent className="py-12 text-center">
      <ImageIcon className="w-12 h-12 text-[#64748B] mx-auto mb-3 opacity-30" strokeWidth={1.5} />
      <p className="text-[#94A3B8]">Sin fotos aun</p>
    </CardContent></Card>;
  }
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="pub-gallery">
        {photos.map((p) => (
          <button key={p.photo_id} onClick={() => setLightbox(p)} className="aspect-square overflow-hidden rounded-lg bg-gray-100 block" data-testid={`pub-photo-${p.photo_id}`}>
            <img src={p.url} alt={p.caption || ""} className="w-full h-full object-cover hover:scale-105 transition-transform" loading="lazy" />
          </button>
        ))}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white"><X className="w-6 h-6" /></button>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}
